/**
 * @description 常用功能函数（时间格式，版本号对比等）
 */

/**
 * @description 获取js数据变量类型
 * @param {*} obj - 任意数据参数
 * @param {string} type - 要判断的类型
 * @returns {string} 如果没传type则返回数据类型[String]（统一小写），否则返回是否为该类型的结果值[Boolean]
 */
const typeOf = function (obj, type) {
  let str = Object.prototype.toString.call(obj);
  str = str.replace(/\[|\]/g, "").split(" ")[1];
  return type ? str.toLowerCase() === type : str.toLowerCase();
};

/**
 * @description 将对参数象转为url字符串参数
 * @param {Object} obj 参数对象
 * @returns {string} 返回url字符串参数
 */
const queryStringify = function (obj) {
  if (typeOf(obj) !== "object") return "";
  return Object.keys(obj).reduce((a, b) => {
    return `${a ? `${a}&` : ""}${b}=${
      b !== "url" ? encodeURIComponent(obj[b]) : obj[b]
    }`;
  }, "");
};

/**
 * @description 时间格式化
 * @param {(Date|string|timestamp)} date 需要转换的日期，选填，默认为new Date()
 * @param {(string|boolean)} format 需要转换的日期格式，规定字符串里的YYYY,MM,DD,hh,mm,ss,Day对应年，月，日，时，分，秒，星期几；若为false，则不做格式输出，直接返回dateMap供自由组装
 * @returns {(string|Object)}   返回格式化后的时间字符串或时间日期粒度对象
 */
const dateFormat = function (date, format = "YYYY-MM-DD") {
  const d = date ? new Date(date) : new Date();
  if (/Invalid Date/i.test(d)) return NaN; //处理无效时间
  const weekday = ["日", "一", "二", "三", "四", "五", "六"];
  const dateMap = {
    YYYY: `${d.getFullYear()}`,
    MM: `${d.getMonth() + 1}`.padStart(2, "0"),
    DD: `${d.getDate()}`.padStart(2, "0"),
    hh: `${d.getHours()}`.padStart(2, "0"),
    mm: `${d.getMinutes()}`.padStart(2, "0"),
    ss: `${d.getSeconds()}`.padStart(2, "0"),
    Day: `${weekday[d.getDay()]}`,
  };
  if (!format) return dateMap;
  return format.replace(/YYYY|MM|DD|hh|mm|ss|Day/g, (key) => dateMap[key]);
};

/**
 * @description 数值处理（大于一千或一万的），单位千:k 万:w
 * @param {(string|number)} num 传入的数值
 * @param {number} [fixed] 保留小数点后多少位，默认为1（不进行四舍五入）
 * @param {boolean} rightZero
 * @returns {(string|number)} 单位化后的数值，少于1000的直接返回原值
 */
const numToUnit = function (num, fixed = 1, rightZero) {
  let mapItem;
  if (isNaN(num) || num < 0) return num;
  const map = {
    k: { value: 1000, unti: "k" },
    w: { value: 10000, unti: "w" },
  };
  if (num >= map.w.value) {
    mapItem = map.w;
  } else if (num >= map.k.value) {
    mapItem = map.k;
  } else {
    return num;
  }
  let strs = String(num / mapItem.value).split(".");
  let left = strs[0] || 0;
  let right = strs[1] ? strs[1].substr(0, fixed) : 0;
  let resNum = right === "0" && !rightZero ? left : `${left}.${right}`;
  return `${resNum}${mapItem.unti}`;
};

/**
 * @description 版本号对比（自定义，非mdd交换方法）
 * @param {string} refer_version 被对比的版本号
 * @param {string} cur_version 当前版本号
 * @returns {boolean}  当前版本号大于或等于对比的版本号则返回true，否则false
 */
const diffVersion = function (refer_version, cur_version, splitSign = ".") {
  if (!refer_version || !cur_version) return false;
  let flag = false;
  let c_arr = cur_version.split(splitSign);
  let r_arr = refer_version.split(splitSign);
  for (let index = 0; index < r_arr.length; index++) {
    if (c_arr[index] > r_arr[index]) {
      flag = true;
      break;
    } else if (c_arr[index] == r_arr[index]) {
      flag = true;
      continue;
    } else {
      flag = false;
      break;
    }
  }
  return flag;
};

/**
 * @description 生成Uuid 唯一识别码（Universally Unique Identifier）
 * @param {boolean} needBar  生成的识别码是否需要横杆，默认fasle不需要
 * @return {string}
 */
const createdUuid = function (needBar = false) {
  let d = new Date().getTime();
  const uuid = "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(
    /[xy]/g,
    function (c) {
      const r = (d + Math.random() * 16) % 16 | 0;
      d = Math.floor(d / 16);
      return (c === "x" ? r : (r & 0x3) | 0x8).toString(16);
    }
  );
  return needBar ? uuid : uuid.replace(/\-/g, "");
};

/**
 * 图片预加载
 * @param {Object} obj 参数对象，方法属性如下：
 * @param {Array}  obj.imgUrls/params.imgs 图片url，必填；
 * @param {number} obj.minTime 最小加载时间 选填，默认0，可选[0-3000]ms；
 * @param {number} obj.maxTime 最大加载时间 选填，默认10000ms；
 * @param {Function(rate, index, context)} obj.progress 加载进度回调 rate：当前进度(%)；index：当前图片index；context：当前图片dom对象；
 * @param {Function(rate, errorNum, loadNum)} obj.complete 加载完成回调 rate：总进度(%)；errorNum：加载出错图片量；loadNum：已加载图片量；
 * @param {Function(index, context)} obj.error 图片加载出错回调 index：当前图片index；context：当前图片dom对象；
 * @param {Function(errorNum, loadNum)} obj.timeout 加载超时回调 errorNum：加载出错图片量；loadNum：已加载图片量；
 * @returns {Promise} resolve then返回的结果为图片加载完成complete方法的参数
 */
const prevLoad = function (obj) {
  if (obj == null) return;
  let { imgUrls = [], imgs = [], minTime = 0, maxTime = 10000 } = obj;
  let loadNum = 0;
  let errorNum = 0;

  imgUrls = imgUrls.concat(imgs);
  let imgUrlsLen = imgUrls.length;

  let asyncLoadImages = new Array(imgUrlsLen);

  let isTimeOut = false;
  let isComplete = false;
  let loadMaxTimer = null;
  let startTime = new Date().getTime();
  let minLoadTime = parseInt(minTime);
  let maxLoadTime = parseInt(maxTime);
  if (minLoadTime >= 3000) minLoadTime = 3000;
  return new Promise((resolve, reject) => {
    // 加载进度监听
    let imgLoadWatcher = function (event, num, index) {
      let context = event.target;
      let eventType = event.type;
      let rate = 0;
      if (isTimeOut) return;
      if (eventType === "error") {
        context.onerror = null;
        obj.error && obj.error.call(this, index, context); // 加载出错
      } else {
        rate = parseInt((num / imgUrlsLen) * 100);
        obj.progress && obj.progress.call(this, rate, index, context); // 加载进度
      }

      if (num == imgUrlsLen) {
        let endTiem = new Date().getTime();
        let loadTime = endTiem - startTime;
        let delayTime = loadTime < minLoadTime ? minLoadTime - loadTime : 0;
        isComplete = true;
        loadMaxTimer && clearTimeout(loadMaxTimer);
        setTimeout(function () {
          obj.complete &&
            obj.complete.call(this, rate, errorNum, loadNum, asyncLoadImages); // 加载完成
          resolve({ rate, errorNum, loadNum, asyncLoadImages });
        }, delayTime);
      }
    };
    // 加载遍历
    imgUrls.forEach(function (url, index) {
      let img = new Image();
      img.onload = function (event) {
        imgLoadWatcher(event, ++loadNum, index);
        asyncLoadImages[index] = this;
      };
      img.onerror = function (event) {
        ++errorNum;
        asyncLoadImages[index] = null;
        imgLoadWatcher(event, ++loadNum, index);
      };
      img.src = url;
    });
    // 加载超时
    if (typeof obj.timeout === "function") {
      loadMaxTimer = setTimeout(function () {
        if (!isComplete) {
          isTimeOut = true;
          obj.timeout(loadNum, errorNum);
          reject({ type: "timeout", loadNum, errorNum });
        }
      }, maxLoadTime);
    }
  });
};

/**
 * @description svga资源预加载
 * @param {Object} downloader  svga插件svga.lite加载器Downloader的实例，必填
 * @param {Object} options 预加载参数集
 * @param {function} options.complete  加载完成时执行，不管是否失败，选填，回调参数(resultMaps：结果集)
 * @param {function} options.progress  加载进度回调函数，不管是否失败，选填，回调参数(rate：进度百分比，file：当前加载资源)
 * @param {function} options.fail  加载失败回调函数，选填，回调参数(err：错误，key：当前资源key)
 */
const svgasPrevload = function (downloader, options) {
  const { sourcesMaps, complete, progress, fail } = options;
  if (downloader == null || sourcesMaps == null) return;
  let loadNum = 0;
  const len = Object.keys(sourcesMaps).length;
  const resultMaps = {};
  Object.keys(sourcesMaps).forEach((key) => {
    const item = sourcesMaps[key];
    if (item == null) return;
    downloader
      .get(item)
      .then((res) => {
        resultMaps[key] = res;
      })
      .catch((err) => {
        resultMaps[key] = null;
        fail && fail(err, key);
      })
      .finally(() => {
        const rate = parseInt((++loadNum / len) * 100);
        progress && progress(rate, resultMaps[key]);
        loadNum === len && complete && complete(resultMaps);
      });
  });
};

/**
 * @description 数组制定两个元素互换位置
 * @param  {Array} dArr 需要处理的数组
 * @param {(number|string)} first  被交换元素的下标
 * @param {(number|string)} last  被交换元素的下标
 * @return {Array}
 * @example
 *
 * exchangeArr([1,2,4],0,1)
 * // => [2, 1, 4]
 */
const exchangeArr = (dArr, first, last) => {
  if (!Array.isArray(dArr)) return [];
  if (typeof first !== "number" && typeof first !== "string") {
    return dArr;
  }
  if (typeof last !== "number" && typeof last !== "string") {
    return dArr;
  }
  let arr = JSON.parse(JSON.stringify(dArr));
  let beforeVal = arr.splice(first, 1)[0];
  arr.splice(last, 0, beforeVal);
  return arr;
};

/**
 * @description 根据key来排序JSONArray
 * @param array {Array} 需要排序的数据
 * @param key {String} 排序的依据key
 * @param order {String}  asc 升序 des 降序，默认升序
 * @return {Array}
 * */
const sortArrByKey = (array, key, order = "asc") => {
  if (!Array.isArray(array)) return [];
  if (!key) {
    throw new Error("error arguments", "key");
  }
  const tmp = deepClone(array);
  return tmp.sort((a, b) => {
    if (
      !a ||
      !b ||
      typeof a[key] === "undefined" ||
      typeof b[key] === "undefined"
    ) {
      // null,undefined,NaN等不参与排序
      return 0;
    }
    const firstValue = a[key];
    const lastValue = b[key];
    return order === "asc" ? firstValue - lastValue : lastValue - firstValue;
  });
};
/**
 * @description深克隆
 * @param source {Object}  要被克隆的对象
 * @returns {Object}
 */
const deepClone = (source) => {
  if (!source && typeof source !== "object") {
    throw new Error("error arguments", "deepClone");
  }
  const targetObj = source.constructor === Array ? [] : {};
  Object.keys(source).forEach((keys) => {
    if (source[keys] && typeof source[keys] === "object") {
      targetObj[keys] = deepClone(source[keys]);
    } else {
      targetObj[keys] = source[keys];
    }
  });
  return targetObj;
};

/**
 * @description 判断是字符串否为json
 * @param str {string}
 * @returns {Boolean}
 */
const isJson = (str) => {
  if (typeof str === "string") {
    try {
      const obj = JSON.parse(str);
      if (typeof obj === "object" && obj) {
        return true;
      } else {
        return false;
      }
    } catch (e) {
      return false;
    }
  }
};

/**
 * 通过User-Agent获取移动设备浏览器和操作系统信息
 * @returns {Object} 包含浏览器名称、版本、操作系统名称和版本的对象
 */
const getMobileDeviceInfo = () => {
  const userAgent = navigator.userAgent || navigator.vendor || window.opera;

  // 默认返回值
  let result = {
    browserName: "未知浏览器",
    browserVersion: "未知版本",
    osName: "未知系统",
    osVersion: "未知版本",
  };

  // 检测操作系统
  if (/android/i.test(userAgent)) {
    result.osName = "Android";
    const androidVersion = userAgent.match(/Android\s([0-9\.]*)/);
    if (androidVersion && androidVersion.length > 1) {
      result.osVersion = androidVersion[1];
    }
  } else if (/iPad|iPhone|iPod/.test(userAgent) && !window.MSStream) {
    result.osName = "iOS";
    const iOSVersion = userAgent.match(/OS\s(\d+)_?(\d+)?_?(\d+)?/);
    if (iOSVersion && iOSVersion.length > 1) {
      result.osVersion =
        iOSVersion[1] +
        "." +
        (iOSVersion[2] || "0") +
        "." +
        (iOSVersion[3] || "0");
    }
  } else if (/WinPhone|IEMobile|WPDesktop/.test(userAgent)) {
    result.osName = "Windows Phone";
    const wpVersion = userAgent.match(/Windows Phone (\d+\.\d+)/);
    if (wpVersion && wpVersion.length > 1) {
      result.osVersion = wpVersion[1];
    }
  }

  // console.log(userAgent)

  // 检测浏览器
  if (/mddios|mddandroid|mddharmony/i.test(userAgent)) {
    const _browserName = userAgent.match(/mddios|mddandroid|mddharmony/i);
    result.browserName = _browserName[0];
  } else if (/SamsungBrowser/i.test(userAgent)) {
    result.browserName = "Samsung Internet";
    const version = userAgent.match(/SamsungBrowser\/([\d.]+)/);
    if (version && version.length > 1) {
      result.browserVersion = version[1];
    }
  } else if (/EdgA/i.test(userAgent)) {
    result.browserName = "Microsoft Edge";
    const version = userAgent.match(/EdgA\/([\d.]+)/);
    if (version && version.length > 1) {
      result.browserVersion = version[1];
    }
  } else if (/FBAV/i.test(userAgent)) {
    result.browserName = "Facebook";
    const version = userAgent.match(/FBAV\/([\d.]+)/);
    if (version && version.length > 1) {
      result.browserVersion = version[1];
    }
  } else if (/Instagram/i.test(userAgent)) {
    result.browserName = "Instagram";
    const version = userAgent.match(/Instagram\/([\d.]+)/);
    if (version && version.length > 1) {
      result.browserVersion = version[1];
    }
  } else if (/OPR/i.test(userAgent)) {
    result.browserName = "Opera Mobile";
    const version = userAgent.match(/OPR\/([\d.]+)/);
    if (version && version.length > 1) {
      result.browserVersion = version[1];
    }
  } else if (/Chrome/i.test(userAgent)) {
    result.browserName = "Google Chrome";
    const version = userAgent.match(/Chrome\/([\d.]+)/);
    if (version && version.length > 1) {
      result.browserVersion = version[1];
    }
  } else if (/CriOS/i.test(userAgent)) {
    result.browserName = "Google Chrome";
    const version = userAgent.match(/CriOS\/([\d.]+)/);
    if (version && version.length > 1) {
      result.browserVersion = version[1];
    }
  } else if (/UCBrowser/i.test(userAgent)) {
    result.browserName = "UC Browser";
    const version = userAgent.match(/UCBrowser\/([\d.]+)/);
    if (version && version.length > 1) {
      result.browserVersion = version[1];
    }
  } else if (/MQQBrowser/i.test(userAgent)) {
    result.browserName = "QQ Browser";
    const version = userAgent.match(/MQQBrowser\/([\d.]+)/);
    if (version && version.length > 1) {
      result.browserVersion = version[1];
    }
  } else if (/MicroMessenger/i.test(userAgent)) {
    result.browserName = "WeChat";
    const version = userAgent.match(/MicroMessenger\/([\d.]+)/);
    if (version && version.length > 1) {
      result.browserVersion = version[1];
    }
  } else if (/Safari/i.test(userAgent) && /Mobile/i.test(userAgent)) {
    result.browserName = "Safari Mobile";
    const version = userAgent.match(/Version\/([\d.]+)/);
    if (version && version.length > 1) {
      result.browserVersion = version[1];
    }
  } else if (/Firefox/i.test(userAgent)) {
    result.browserName = "Firefox";
    const version = userAgent.match(/Firefox\/([\d.]+)/);
    if (version && version.length > 1) {
      result.browserVersion = version[1];
    }
  }

  return result;
};

const createSessionId = () => {
  const time = new Date().getTime();
  let res = `${time}_`;
  const createdNum = () => Math.floor(Math.random() * 10);
  for (let i = 0; i < 6; i++) {
    res += createdNum();
  }
  return res;
};

/**
 * @description 生成Uuid 唯一识别码（Universally Unique Identifier）
 * @param {boolean} needBar  生成的识别码是否需要横杆，默认fasle不需要
 * @return {string}
 */
const createUuid = (needBar = false) => {
  let d = new Date().getTime();
  if (
    window &&
    window.performance &&
    typeof window.performance.now === "function"
  ) {
    d += window.performance.now();
  }
  const base = "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx";
  const uuid = base.replace(/[xy]/g, function (c) {
    const r = (d + Math.random() * 16) % 16 | 0;
    d = Math.floor(d / 16);
    return (c === "x" ? r : (r & 0x3) | 0x8).toString(16);
  });
  return needBar ? uuid : uuid.replace(/-/g, "");
};

const http2https = (src) => {
  if (!src) return src;
  src = src.replace(/^http:\/\//gi, "https://");
  return src;
};

export {
  isJson,
  typeOf,
  queryStringify,
  dateFormat,
  numToUnit,
  diffVersion,
  createdUuid,
  prevLoad,
  svgasPrevload,
  exchangeArr,
  sortArrByKey,
  deepClone,
  getMobileDeviceInfo,
  createSessionId,
  createUuid,
  http2https,
};
